"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { AlertCircleIcon, SearchIcon, SettingsIcon } from "lucide-react";
import Link from "next/link";
import React, { useActionState, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { useTrackEvent } from "@/components/analytics/events";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ColorPicker, DEFAULT_COLORS } from "@/components/ui/color-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { isDemoMode } from "@/config";
import { useRouter } from "@/lib/navigation";
import { useRefreshWorkspaceSidebar } from "../../_contexts/workspace-sidebar-data-context";
import {
  createProject,
  createSearchAnalyticsOnboardingIntent,
  fetchSearchAnalyticsBigQueries,
} from "../../action";
import { serializeBigQuerySearchParams } from "../../settings/_search-params/bigquery-search-params";

type BigQueryConfig = {
  id: string;
  projectID: string;
  datasetName: string;
  latestDataDate?: string;
};

type Props = {
  workspaceId: string;
  trigger: React.ReactNode;
  searchAnalyticsBigQueries?: BigQueryConfig[];
  workspaceUsedColors?: string[];
};

export default function CreateSAProjectDialog({
  workspaceId,
  trigger,
  searchAnalyticsBigQueries = [],
  workspaceUsedColors,
}: Props) {
  const trackEvent = useTrackEvent();
  const { i18n } = useLingui();
  const router = useRouter();
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();
  const encodedWorkspaceId = encodeURIComponent(workspaceId);
  const addBigQueryHref = serializeBigQuerySearchParams(
    `/${i18n.locale}/${encodedWorkspaceId}/settings/sa-bigquery`,
    { addDataset: "1" },
  );
  const title = i18n._(msg`Create Search Analytics project`);
  const description = i18n._(
    msg`Choose the BigQuery dataset that provides Search Console data.`,
  );

  const [open, setOpen] = useState(false);
  const [selectedBigQueryId, setSelectedBigQueryId] = useState<string>("");
  const [bigQueries, setBigQueries] = useState<BigQueryConfig[]>(
    searchAnalyticsBigQueries,
  );
  const [loadingBigQueries, setLoadingBigQueries] = useState(false);
  const [hasFetched, setHasFetched] = useState(
    searchAnalyticsBigQueries.length > 0,
  );
  const [pendingOpen, setPendingOpen] = useState(false);
  const fetchingRef = useRef(false);

  React.useEffect(() => {
    if (pendingOpen && hasFetched && !loadingBigQueries) {
      setOpen(true);
      setPendingOpen(false);
    }
  }, [pendingOpen, hasFetched, loadingBigQueries]);

  const handleTriggerClick = () => {
    if (!hasFetched && !fetchingRef.current) {
      setPendingOpen(true);
      fetchingRef.current = true;
      setLoadingBigQueries(true);
      fetchSearchAnalyticsBigQueries(workspaceId)
        .then((data) => {
          setBigQueries(data);
          setHasFetched(true);
        })
        .catch((error) => {
          console.error("Failed to fetch BigQuery configurations:", error);
          setHasFetched(true);
        })
        .finally(() => {
          setLoadingBigQueries(false);
          fetchingRef.current = false;
        });
    } else {
      setOpen(true);
    }
  };

  React.useEffect(() => {
    if (!open) {
      setSelectedBigQueryId("");
      setPendingOpen(false);
      if (searchAnalyticsBigQueries.length === 0) {
        setHasFetched(false);
        setLoadingBigQueries(false);
        fetchingRef.current = false;
      }
    }
  }, [open, searchAnalyticsBigQueries.length]);

  const [state, action, pending] = useActionState<
    {
      errors: string[];
      projectId?: string;
    },
    FormData
  >(
    async (_, formData) => {
      const name = formData.get("name");
      const color = formData.get("color");
      if (typeof name !== "string") {
        return { errors: ["Invalid form data"] };
      }
      if (typeof color !== "string" || !color) {
        return { errors: ["Color selection is required"] };
      }
      if (!selectedBigQueryId) {
        return { errors: ["BigQuery configuration is required"] };
      }
      const result = await createProject(
        workspaceId,
        name,
        "sa",
        color,
        selectedBigQueryId,
      );

      // プロジェクト作成後の初期分析準備だけ失敗しても、作成済み project へ進ませて重複作成を避けます。
      if (result.projectId) {
        const intent = formData.get("intent");
        let shouldOpenOnboarding = intent === "create-and-analyze";
        let hasSetupError = result.errors.length > 0;
        if (result.errors.length > 0) {
          shouldOpenOnboarding = false;
          toast.error(result.errors.join("\n"));
        }
        if (shouldOpenOnboarding) {
          const intentResult = await createSearchAnalyticsOnboardingIntent(
            workspaceId,
            result.projectId,
          );
          if (intentResult.errors.length > 0) {
            shouldOpenOnboarding = false;
            hasSetupError = true;
            toast.error(intentResult.errors.join("\n"));
          }
        }
        if (!hasSetupError) {
          trackEvent({
            eventName: "sa_project_created",
            payload: { product_area: "sa" },
          });
        }
        const projectPath = shouldOpenOnboarding
          ? `/${i18n.locale}/${encodedWorkspaceId}/sa/${encodeURIComponent(result.projectId)}/onboarding`
          : `/${i18n.locale}/${encodedWorkspaceId}/sa/${encodeURIComponent(result.projectId)}`;
        router.push(projectPath);
        router.refresh();

        // 成功メッセージを表示
        if (!hasSetupError) {
          toast.success(i18n._(msg`Search Analytics project created`));
        }

        // ダイアログを閉じる
        setOpen(false);
        refreshWorkspaceSidebar("project-mutation");
      }

      return result;
    },
    {
      errors: [],
    },
  );

  const id = useId();
  const selectedBigQuery = bigQueries.find(
    (bigQuery) => bigQuery.id === selectedBigQueryId,
  );
  const selectedBigQueryIsReady = Boolean(selectedBigQuery?.latestDataDate);

  const getRandomDefaultColor = () => {
    return DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)];
  };

  return (
    <>
      {React.cloneElement(
        trigger as React.ReactElement<{
          onClick?: () => void;
          disabled?: boolean;
        }>,
        {
          onClick: handleTriggerClick,
          disabled:
            (trigger as React.ReactElement<{ disabled?: boolean }>).props
              .disabled || loadingBigQueries,
        },
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <form action={action}>
            <div className="flex flex-col gap-4">
              {hasFetched && bigQueries.length === 0 && (
                <div className="rounded-lg border bg-muted/20 p-4">
                  <div className="flex gap-3">
                    <SearchIcon
                      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <div className="flex flex-col gap-3">
                      <div>
                        <p className="text-sm font-medium">
                          <Trans>No BigQuery dataset is connected yet</Trans>
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          <Trans>
                            Run a lightweight Search Console analysis first, or
                            connect BigQuery before creating a project.
                          </Trans>
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button asChild size="sm">
                          <Link
                            href={`/${i18n.locale}/${encodedWorkspaceId}/onboarding/search-analytics`}
                            onClick={() => {
                              setOpen(false);
                            }}
                          >
                            <Trans>Try initial analysis</Trans>
                          </Link>
                        </Button>
                        <Button asChild variant="outline" size="sm">
                          <Link
                            href={addBigQueryHref}
                            onClick={() => {
                              setOpen(false);
                            }}
                          >
                            <Trans>Connect BigQuery</Trans>
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Label htmlFor={`${id}-name`}>
                  <Trans>Project name</Trans>
                </Label>
                <Input
                  id={`${id}-name`}
                  name="name"
                  required
                  disabled={hasFetched && bigQueries.length === 0}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>
                  <Trans>Project color</Trans>
                </Label>
                <ColorPicker
                  name="color"
                  defaultValue={getRandomDefaultColor()}
                  usedColors={workspaceUsedColors}
                  required
                  disabled={hasFetched && bigQueries.length === 0}
                />
              </div>
              {hasFetched && bigQueries.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`${id}-bigquery`}>BigQuery</Label>
                    <Link
                      href={addBigQueryHref}
                      onClick={() => {
                        setOpen(false);
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-medium leading-none text-primary underline-offset-4 hover:underline"
                    >
                      <SettingsIcon
                        className="size-3.5 shrink-0"
                        aria-hidden="true"
                      />
                      <span>
                        <Trans>Settings</Trans>
                      </span>
                    </Link>
                  </div>
                  <Select
                    value={selectedBigQueryId}
                    onValueChange={setSelectedBigQueryId}
                  >
                    <SelectTrigger
                      id={`${id}-bigquery`}
                      className="items-start text-start h-auto whitespace-normal [&>span]:line-clamp-4 [&>span]:whitespace-normal [&>span]:wrap-break-all"
                    >
                      <SelectValue className="text-left leading-5 wrap-break-all" />
                    </SelectTrigger>
                    <SelectContent>
                      {bigQueries.map((bq) => {
                        const noData = !bq.latestDataDate;
                        return (
                          <SelectItem
                            key={bq.id}
                            value={bq.id}
                            disabled={noData}
                            className="items-start whitespace-normal leading-5"
                          >
                            <div className="w-full wrap-break-words">
                              {bq.projectID} / {bq.datasetName}
                              {noData && (
                                <FieldError className="mt-1 text-xs">
                                  {i18n._(
                                    msg`Please check the BigQuery dataset setting`,
                                  )}
                                </FieldError>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {state.errors.length > 0 && (
              <Alert className="mt-4" variant="destructive">
                <AlertCircleIcon className="h-4 w-4" />
                <AlertTitle>
                  <Trans>Error</Trans>
                </AlertTitle>
                <AlertDescription>
                  <ul>
                    {state.errors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter className="mt-6 gap-2 sm:justify-end">
              <Button
                type="submit"
                name="intent"
                value="create-only"
                variant="outline"
                disabled={
                  pending ||
                  isDemoMode ||
                  (hasFetched && bigQueries.length === 0) ||
                  (hasFetched &&
                    bigQueries.length > 0 &&
                    (!selectedBigQueryId || !selectedBigQueryIsReady))
                }
              >
                <Trans>Create project</Trans>
              </Button>
              <Button
                type="submit"
                name="intent"
                value="create-and-analyze"
                disabled={
                  pending ||
                  isDemoMode ||
                  (hasFetched && bigQueries.length === 0) ||
                  (hasFetched &&
                    bigQueries.length > 0 &&
                    (!selectedBigQueryId || !selectedBigQueryIsReady))
                }
              >
                {pending ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <Trans>Create and start initial analysis</Trans>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
